/* OBFUSCATED CODE - INTELLECTUAL PROPERTY PROTECTED */
'use client';
import { useEffect, useCallback } from 'react';
import { calculateDynamicParams } from './sprayPresets';
import { rollerDimensions } from './sprayAlgorithms.roller';
import { getTranslation } from './i18n';
export function useCanManager({
    cans,
    setCans,
    activeCanId,
    setActiveCanId,
    currentColor,
    setCurrentColor,
    activePreset,
    userOverrides,
    presets,
    showColorPickerFor,
    setShowColorPickerFor,
    tempColor,
    setTempColor,
    paramSetters,
    toolbarSetters,
    onEnterPreviewMode,
    onEnterMultiPreviewMode,
    isPreviewMode,
    cancelPreviewMode: cancelPreviewModeRef,
    previewCanId,
    lang = 'en',
}) {
    useEffect(() => {
        window.updatePaintLeft = (canId, newPaintLeft) => {
            setCans(prev => prev.map(c =>
                c.id === canId ? { ...c, paintLeft: newPaintLeft, used: (newPaintLeft < 10000) ? true : c.used } : c
            ));
        };
        return () => { window.updatePaintLeft = null; };
    }, [setCans]);
    const resolveParams = useCallback((paintLeft, presetKey, can) => {
        const dynamicParams = calculateDynamicParams(paintLeft, presetKey);
        const selectedPreset = presets[presetKey];
        const overrides = userOverrides[presetKey] || {};
        const finalRadius = overrides.radius ?? dynamicParams.radius;
        const finalDensity = overrides.density ?? dynamicParams.density;
        const finalSpeedFactor = overrides.speedFactor ?? dynamicParams.speedFactor;
        const finalScale = overrides.scale ?? selectedPreset.scaleVal;
        let effectivePreset = selectedPreset;
        if (can?.type === 'roller') {
            const dimensions = rollerDimensions[presetKey] || rollerDimensions.Big;
            effectivePreset = {
                ...selectedPreset,
                rollerWidth: dimensions.width,
                rollerHeight: dimensions.height,
                enableDrips: false,
            };
        }
        return { finalRadius, finalDensity, finalSpeedFactor, finalScale, effectivePreset };
    }, [presets, userOverrides]);
    const selectCan = useCallback((canId, options = {}) => {
        const { autoSwitchMode = false } = options;
        const selectedCan = cans.find(can => can.id === canId);
        if (!selectedCan) return;
        if (isPreviewMode && canId !== previewCanId) {
            if (selectedCan.color) {
                if (cancelPreviewModeRef && typeof cancelPreviewModeRef.current === 'function') cancelPreviewModeRef.current();
            } else {
                if (cancelPreviewModeRef && typeof cancelPreviewModeRef.current === 'function') cancelPreviewModeRef.current();
                if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('closeMobileSheets'));
                if (!autoSwitchMode) {
                    setShowColorPickerFor(canId);
                    setTempColor('#ff3366');
                }
                return;
            }
        }
        if (isPreviewMode && canId === previewCanId) return;
        if (canId === activeCanId) {
            if (!autoSwitchMode && (selectedCan.paintLeft === 10000 || selectedCan.paintLeft === 0)) {
                if (selectedCan.paintLeft === 10000 && !selectedCan.used) {
                    setShowColorPickerFor(canId);
                    setTempColor(selectedCan.color || '#ff3366');
                    if (typeof window !== 'undefined') {
                        const evt = new CustomEvent('closeMobileSheets');
                        window.dispatchEvent(evt);
                    }
                } else {
                    if (window.showToast) {
                        const msg = getTranslation(lang, 'cannot_swap_used');
                        window.showToast(msg, '🚨');
                    }
                }
            }
            return;
        }
        if (!selectedCan.color) {
            if (!autoSwitchMode) {
                setShowColorPickerFor(canId);
                setTempColor('#ff3366');
                if (typeof window !== 'undefined') {
                    const evt = new CustomEvent('closeMobileSheets');
                    window.dispatchEvent(evt);
                }
            } else {
                const updatedCans = cans.map(can =>
                    can.id === canId ? { ...can, isActive: true } : { ...can, isActive: false }
                );
                setCans(updatedCans);
                setActiveCanId(canId);
                if (window.syncPaintState) window.syncPaintState(updatedCans);
            }
            return;
        }
        const updatedCans = cans.map(can =>
            can.id === canId ? { ...can, isActive: true } : { ...can, isActive: false }
        );
        setCans(updatedCans);
        setActiveCanId(canId);
        setCurrentColor(selectedCan.color);
        if (selectedCan.type === 'spray') {
            toolbarSetters.setToolbarSprayColor(selectedCan.color);
            toolbarSetters.setToolbarSprayPct(Math.round((selectedCan.paintLeft / 10000) * 100));
        } else if (selectedCan.type === 'roller') {
            toolbarSetters.setToolbarRollerColor(selectedCan.color);
        }
        if (window.syncPaintState) window.syncPaintState(updatedCans);
        if (window.updateSprayColor) window.updateSprayColor(selectedCan.color);
        const { finalRadius, finalDensity, finalSpeedFactor, finalScale, effectivePreset } =
            resolveParams(selectedCan.paintLeft, activePreset, selectedCan);
        paramSetters.setActualRadius(finalRadius);
        paramSetters.setActualDensity(finalDensity);
        paramSetters.setActualSpeed(finalScale);
        paramSetters.setActualSpeedFactor(finalSpeedFactor);
        paramSetters.setRadiusVal(finalRadius);
        paramSetters.setDensityVal(finalDensity);
        paramSetters.setSpeedFactorVal(finalSpeedFactor);
        paramSetters.setScaleVal(finalScale);
        if (window.updateSprayParams) {
            window.updateSprayParams(
                finalRadius, finalDensity, finalScale, finalSpeedFactor,
                activePreset,
                effectivePreset.enableDrips || false,
                effectivePreset.paintConsumption || 1,
                effectivePreset,
                canId, selectedCan.color, updatedCans
            );
        }
    }, [cans, activePreset, userOverrides, resolveParams,
        isPreviewMode, cancelPreviewModeRef, previewCanId,
        setCans, setActiveCanId, setCurrentColor, setShowColorPickerFor, setTempColor,
        paramSetters, toolbarSetters, lang]);
    const applySelectedColor = useCallback((overrideColor, forcedCanId = null, markAsPaid = false) => {
        const colorToApply = typeof overrideColor === 'string' ? overrideColor : tempColor;
        const targetCanId = forcedCanId || showColorPickerFor;
        if (!targetCanId || !colorToApply) return;
        const targetCan = cans.find(can => can.id === targetCanId);
        if (!targetCan) return;
        const isTargetReplaceable = !targetCan.color || (targetCan.paintLeft === 10000 && !targetCan.used);
        if (!isTargetReplaceable) {
            if (window.showToast) {
                const msg = getTranslation(lang, 'cannot_swap_used');
                window.showToast(msg, '🚨');
            }
            return;
        }
        if (!forcedCanId && typeof onEnterPreviewMode === 'function') {
            onEnterPreviewMode(colorToApply, targetCanId, targetCan);
            setShowColorPickerFor(null);
            return;
        }
        const updatedCans = cans.map(can =>
            can.id === targetCanId
                ? {
                    ...can,
                    color: colorToApply,
                    isActive: true,
                    used: false,
                    paintLeft: targetCan && typeof targetCan.paintLeft === 'number' ? targetCan.paintLeft : 10000,
                    isPaid: markAsPaid ? true : can.isPaid
                }
                : can.id === activeCanId
                    ? { ...can, isActive: false }
                    : can
        );
        setCans(updatedCans);
        setActiveCanId(targetCanId);
        setCurrentColor(colorToApply);
        if (window.syncPaintState) window.syncPaintState(updatedCans);
        if (window.updateSprayColor) window.updateSprayColor(colorToApply);
        const paintLeft = targetCan && typeof targetCan.paintLeft === 'number' ? targetCan.paintLeft : 10000;
        const { finalRadius, finalDensity, finalSpeedFactor, finalScale, effectivePreset } =
            resolveParams(paintLeft, activePreset, targetCan);
        paramSetters.setActualRadius(finalRadius);
        paramSetters.setActualDensity(finalDensity);
        paramSetters.setActualSpeed(finalScale);
        paramSetters.setActualSpeedFactor(finalSpeedFactor);
        paramSetters.setRadiusVal(finalRadius);
        paramSetters.setDensityVal(finalDensity);
        paramSetters.setSpeedFactorVal(finalSpeedFactor);
        paramSetters.setScaleVal(finalScale);
        if (window.updateSprayParams) {
            window.updateSprayParams(
                finalRadius, finalDensity, finalScale, finalSpeedFactor,
                activePreset,
                effectivePreset.enableDrips || false,
                effectivePreset.paintConsumption || 1,
                effectivePreset,
                targetCanId, colorToApply, updatedCans
            );
        }
        if (targetCan?.type === 'spray') {
            toolbarSetters.setToolbarSprayColor(colorToApply);
            toolbarSetters.setToolbarSprayPct(Math.round((paintLeft / 10000) * 100));
        } else if (targetCan?.type === 'roller') {
            toolbarSetters.setToolbarRollerColor(colorToApply);
        }
        setShowColorPickerFor(null);
    }, [showColorPickerFor, tempColor, cans, activeCanId, activePreset, userOverrides,
        resolveParams, setCans, setActiveCanId, setCurrentColor, setShowColorPickerFor,
        paramSetters, toolbarSetters, onEnterPreviewMode, lang]);
    const cancelColorSelection = useCallback(() => {
        setShowColorPickerFor(null);
    }, [setShowColorPickerFor]);
    const addNewCan = useCallback(() => {
        const newId = Math.max(...cans.map(c => c.id)) + 1;
        if (newId > 30) {
            alert('Максимальное количество баллонов: 30');
            return;
        }
        const updatedCans = [...cans, {
            id: newId,
            color: null,
            paintLeft: 10000,
            isActive: false,
            type: 'spray'
        }];
        setCans(updatedCans);
        if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('closeMobileSheets'));
        setShowColorPickerFor(newId);
        if (window.syncPaintState) window.syncPaintState(updatedCans);
    }, [cans, setCans, setShowColorPickerFor]);
    const applyCustomColorBatch = useCallback((validCustomColors, forcedTargetCanId = null, markAsPaid = false) => {
        if (!validCustomColors || validCustomColors.length === 0) return;
        const currentTargetCanId = forcedTargetCanId || showColorPickerFor;
        let remainingColorsToApply = [...validCustomColors];
        let newMaxId = Math.max(...cans.map(c => c.id), 0);
        let updatedCans = [...cans];
        let hasChanges = false;
        const targetCanIndex = updatedCans.findIndex(c => c.id === currentTargetCanId);
        const targetCan = targetCanIndex !== -1 ? updatedCans[targetCanIndex] : null;
        const targetType = targetCan?.type || 'spray';
        if (typeof onEnterMultiPreviewMode === 'function' && showColorPickerFor) {
            onEnterMultiPreviewMode(validCustomColors, showColorPickerFor, targetCan);
            setShowColorPickerFor(null);
            return;
        }
        if (targetCan && remainingColorsToApply.length > 0) {
            const isTargetReplaceable = !targetCan.color || (targetCan.paintLeft === 10000 && !targetCan.used);
            if (isTargetReplaceable) {
                const newColorObj = remainingColorsToApply.shift();
                updatedCans[targetCanIndex] = {
                    ...targetCan,
                    color: newColorObj.hex,
                    name: newColorObj.name || targetCan.name,
                    paintLeft: 10000,
                    used: false,
                    isPaid: markAsPaid ? true : targetCan.isPaid
                };
                hasChanges = true;
                if (updatedCans[targetCanIndex].id === activeCanId) {
                    setCurrentColor(newColorObj.hex);
                    if (window.updateSprayColor) window.updateSprayColor(newColorObj.hex);
                }
            }
        }
        for (let i = 0; i < updatedCans.length; i++) {
            if (remainingColorsToApply.length === 0) break;
            const can = updatedCans[i];
            const canType = can.type || 'spray';
            if (canType !== targetType || can.id === currentTargetCanId) continue;
            const isMassFillable = !can.color || (can.paintLeft === 10000 && !can.used); 
            if (isMassFillable) {
                const newColorObj = remainingColorsToApply.shift();
                updatedCans[i] = {
                    ...can,
                    color: newColorObj.hex,
                    name: newColorObj.name || can.name,
                    paintLeft: 10000,
                    used: false,
                    isPaid: markAsPaid ? true : can.isPaid
                };
                hasChanges = true;
                if (updatedCans[i].id === activeCanId) {
                    setCurrentColor(newColorObj.hex);
                    if (window.updateSprayColor) window.updateSprayColor(newColorObj.hex);
                }
            }
        }
        if (targetType === 'spray') {
            while (remainingColorsToApply.length > 0 && updatedCans.length < 30) {
                const nextId = newMaxId + 1;
                newMaxId = nextId;
                const newColorObj = remainingColorsToApply.shift();
                updatedCans.push({
                    id: nextId,
                    color: newColorObj.hex,
                    name: newColorObj.name || '',
                    paintLeft: 10000,
                    isActive: false,
                    type: 'spray',
                    used: false,
                    isPaid: markAsPaid ? true : false
                });
                hasChanges = true;
            }
        }
        if (hasChanges) {
            setCans(updatedCans);
            if (window.syncPaintState) window.syncPaintState(updatedCans);
        }
        if (showColorPickerFor) setShowColorPickerFor(null);
    }, [cans, activeCanId, setCans, setCurrentColor, setShowColorPickerFor, showColorPickerFor, onEnterMultiPreviewMode]);
    return {
        selectCan,
        applySelectedColor,
        applyCustomColorBatch,
        cancelColorSelection,
        addNewCan,
    };
}
